import type { Task } from "@common/types/task";
import { TaskColumn } from "@common/types/views/tasksView";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { hasFieldContent } from "@design-system/Board/BoardCardLayout";
import { taskViewConfig } from "../taskViewConfig";
import { TaskAddFieldChip, type TaskAddFieldState } from "./TaskAddFieldChip";
import {
    TaskFieldControl,
    type TaskFieldEditor,
} from "./TaskBoardFieldControls";
import { TaskBoardShortcutAnchor } from "./TaskBoardShortcutAnchor";
import {
    TaskChipButton,
    type TaskChipField,
    type TaskChipLook,
} from "./TaskChipButton";
import {
    isTaskFieldEditable,
    TASK_CARD_FIELD_LABELS,
    type TaskCardEditField,
} from "./taskCardEdits";
import { TaskStatusPill } from "./TaskStatusPill";
import styles from "./TaskBoardCard.module.scss";

const ADD_FIELD_ID = "addField";

/** A field a card edits as a chip; the status and assignee have their own. */
type TaskCardChipField = Exclude<TaskCardEditField, "status" | "assignee">;

interface ChipSpec {
    field: TaskCardChipField;
    look: TaskChipLook;
}

/** The card fields that edit from the card, by `TaskColumn` id. */
export const TASK_CARD_CHIP_SPECS = new Map<string, ChipSpec>([
    [TaskColumn.DUE_DATE, { field: "dueDate", look: "chip" }],
    [TaskColumn.ENTITIES, { field: "entities", look: "bare" }],
    [TaskColumn.PRIORITY, { field: "priority", look: "chip" }],
    [TaskColumn.ESTIMATE, { field: "estimate", look: "chip" }],
    [TaskColumn.GROUP, { field: "group", look: "chip" }],
    [TaskColumn.CLOSE_STAGE, { field: "stage", look: "chip" }],
    [TaskColumn.CLOSE, { field: "service", look: "chip" }],
]);

/** The chip fields with their columns in List's column order, as its column settings list hidden columns. */
const LIST_ORDERED_CHIP_SPECS = Object.keys(taskViewConfig.columns).flatMap(
    (id) => {
        const spec = TASK_CARD_CHIP_SPECS.get(id);
        return spec === undefined ? [] : [{ id, ...spec }];
    },
);

/** Whether `task` has a value for a field its card doesn't show. */
const HAS_FIELD_VALUE: Record<TaskCardChipField, (task: Task) => boolean> = {
    dueDate: ({ dueDate }) => !!dueDate,
    entities: ({ entityIds }) => !!entityIds?.length,
    priority: ({ priority }) => !!priority,
    estimate: ({ estimatedTime }) => !!estimatedTime,
    group: ({ groupId }) => !!groupId,
    stage: ({ closeStageId }) => !!closeStageId,
    service: ({ closeId }) => !!closeId,
};

interface CardShownFields {
    /** The card's fields. */
    fields: readonly BoardCardField[];
    /** The `TaskColumn` ids the columns and lanes show. */
    axisFields: ReadonlySet<string>;
}

/** The chip fields, in List's order, that neither the card nor its columns and lanes show. */
const getUnshownSpecs = ({ fields, axisFields }: CardShownFields) => {
    const shownIds = new Set(fields.map(({ id }) => id));
    return LIST_ORDERED_CHIP_SPECS.filter(
        ({ id }) => !shownIds.has(id) && !axisFields.has(id),
    );
};

/**
 * `editor`, confirming a save of `field` with an `Updated <field>` toast
 * when neither the card nor its columns and lanes show it, since the card
 * doesn't change. Used by the Add field menu and the card shortcuts.
 */
export const getConfirmingEditor = ({
    editor,
    field,
    ...shown
}: CardShownFields & {
    editor: TaskFieldEditor;
    field: TaskCardEditField;
}): TaskFieldEditor =>
    getUnshownSpecs(shown).some((spec) => spec.field === field)
        ? {
              ...editor,
              onEdit: (edit) =>
                  editor.onEdit({
                      ...edit,
                      confirmation: `Updated ${TASK_CARD_FIELD_LABELS[field].toLowerCase()}`,
                  }),
          }
        : editor;

/**
 * A field's chip. One whose picker is open stays mounted once its field
 * empties (and out of Add field until it closes), so the picker stays open.
 */
const getChipField = ({
    cardField,
    spec,
    editor,
    openField,
}: {
    cardField: BoardCardField;
    spec: ChipSpec;
    editor: TaskFieldEditor;
    openField: TaskChipField | null;
}): BoardCardField => ({
    ...cardField,
    content: (hasFieldContent(cardField.content) ||
        openField === spec.field) && (
        <TaskFieldControl
            field={spec.field}
            editor={editor}
            renderTrigger={(props) => (
                <TaskChipButton {...props} field={spec.field} look={spec.look}>
                    {cardField.content}
                </TaskChipButton>
            )}
        />
    ),
});

/**
 * The card's fields with each editable one as a chip that opens List's
 * control for it, then the Add field chip while an editable field has no
 * value (or one picked from it is still open): the shown ones in card
 * order, then the rest in List's order, minus those the columns and lanes
 * show. Saving one the card doesn't show toasts (`getConfirmingEditor`).
 * The Add field chip follows the tags and their +N and doesn't
 * count toward the three shown; a card with nothing under its title gets a
 * row just for it, on purpose. An entity-locked task keeps its entities
 * read only, and Stage and Service edit only where the page offers them.
 */
export const getTaskCardChipFields = ({
    fields,
    editor,
    adding,
    openField,
    axisFields,
    unofferedFields,
}: {
    fields: readonly BoardCardField[];
    editor: TaskFieldEditor;
    adding: TaskAddFieldState;
    /** The field whose chip has its picker open (`useOpenTaskFieldChip`). */
    openField: TaskChipField | null;
    /** The `TaskColumn` ids the columns and lanes show. */
    axisFields: ReadonlySet<string>;
    /** The fields the page can't edit (`useUnofferedTaskFields`). */
    unofferedFields: ReadonlySet<TaskCardEditField>;
}): readonly BoardCardField[] => {
    const { task } = editor;
    const isEditable = (field: TaskCardEditField) =>
        isTaskFieldEditable({ task, field }) && !unofferedFields.has(field);
    const emptyFields: TaskCardEditField[] = [];
    const chipFields = fields.map((cardField) => {
        const spec = TASK_CARD_CHIP_SPECS.get(cardField.id);
        if (spec === undefined || !isEditable(spec.field)) {
            return cardField;
        }
        if (!hasFieldContent(cardField.content) && openField !== spec.field) {
            emptyFields.push(spec.field);
        }
        return getChipField({ cardField, spec, editor, openField });
    });
    const unshownFields = getUnshownSpecs({ fields, axisFields })
        .filter(
            ({ field }) => isEditable(field) && !HAS_FIELD_VALUE[field](task),
        )
        .map(({ field }) => field);
    return [
        ...chipFields,
        {
            id: ADD_FIELD_ID,
            placement: "inlineEnd",
            content: (emptyFields.length > 0 ||
                unshownFields.length > 0 ||
                adding.field !== null) && (
                <TaskAddFieldChip
                    emptyFields={emptyFields}
                    unshownFields={unshownFields}
                    renderControl={(field, onEnd) => (
                        <TaskBoardShortcutAnchor
                            field={field}
                            editor={getConfirmingEditor({
                                editor,
                                field,
                                fields,
                                axisFields,
                            })}
                            onEnd={onEnd}
                        />
                    )}
                    adding={adding}
                />
            ),
        },
    ];
};

/** The status pill as the card's first tag, opening List's status control. */
export const TaskStatusChip = ({ editor }: { editor: TaskFieldEditor }) => (
    <TaskFieldControl
        field="status"
        editor={editor}
        renderTrigger={(props) => (
            <TaskChipButton {...props} field="status">
                <TaskStatusPill
                    status={editor.task.status}
                    className={styles.statusPill}
                />
            </TaskChipButton>
        )}
    />
);
